import { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { useLocation, useSearch } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  Shield, Building2, DollarSign, Brain, Settings, MessageSquare, Layers,
  BarChart2, Bug, Sparkles, Users, ChevronRight, ChevronLeft, Eye, EyeOff,
  LayoutDashboard, Store, BookOpen, ShieldCheck, Activity, Database,
  type LucideIcon,
} from "lucide-react";
import AiIntelligencePage from "@/pages/ai-intelligence";
import { adminFetch } from "./admin/shared";
import type { AdminUser, CommissionRow, AdminSection } from "./admin/shared";

// ─── Lazy-loaded section components ──────────────────────────────────────────

const CommandCenterSection = lazy(() =>
  import("./admin/command-center").then((m) => ({ default: m.CommandCenterSection }))
);
const AIInsightsSection = lazy(() =>
  import("./admin/ai-insights").then((m) => ({ default: m.AIInsightsSection }))
);
const DealersSection = lazy(() =>
  import("./admin/dealers").then((m) => ({ default: m.DealersSection }))
);
const SalespeopleSection = lazy(() =>
  import("./admin/salespeople").then((m) => ({ default: m.SalespeopleSection }))
);
const CommissionsSection = lazy(() =>
  import("./admin/commissions").then((m) => ({ default: m.CommissionsSection }))
);
const RbacTab = lazy(() =>
  import("./admin/rbac").then((m) => ({ default: m.RbacTab }))
);
const PricingTab = lazy(() =>
  import("./admin/pricing").then((m) => ({ default: m.PricingTab }))
);
const MessagingTab = lazy(() =>
  import("./admin/messaging").then((m) => ({ default: m.MessagingTab }))
);
const VendorDirectoryTab = lazy(() =>
  import("./admin/vendor-directory").then((m) => ({ default: m.VendorDirectoryTab }))
);
const FeedbackTab = lazy(() =>
  import("./admin/feedback").then((m) => ({ default: m.FeedbackTab }))
);
const DealerFeedTab = lazy(() =>
  import("./admin/dealer-feed").then((m) => ({ default: m.DealerFeedTab }))
);
const SalesTeamsTab = lazy(() =>
  import("./admin/sales-teams").then((m) => ({ default: m.SalesTeamsTab }))
);
const CompanyPerformanceTab = lazy(() =>
  import("./admin/company-performance").then((m) => ({ default: m.CompanyPerformanceTab }))
);
const UploadDiagnosticsSection = lazy(() =>
  import("./admin/upload-diagnostics").then((m) => ({ default: m.UploadDiagnosticsSection }))
);
const ApiDocsSection = lazy(() =>
  import("./admin/api-docs").then((m) => ({ default: m.ApiDocsSection }))
);
const EnvHealthSection = lazy(() =>
  import("./admin/env-health").then((m) => ({ default: m.EnvHealthSection }))
);
const CodesSection = lazy(() =>
  import("./admin/codes").then((m) => ({ default: m.CodesSection }))
);
const DealerAnalyticsSection = lazy(() =>
  import("./admin/dealer-analytics").then((m) => ({ default: m.DealerAnalyticsSection }))
);
const TelemetrySection = lazy(() =>
  import("./admin/telemetry").then((m) => ({ default: m.TelemetrySection }))
);
const DatabaseTrainingSection = lazy(() =>
  import("./admin/database-training").then((m) => ({ default: m.DatabaseTrainingSection }))
);

// ─── Nav ─────────────────────────────────────────────────────────────────────

const NAV_ITEMS: { id: AdminSection; label: string; icon: any; superAdminOnly?: boolean }[] = [
  { id: "command-center", label: "Command Center", icon: LayoutDashboard },
  { id: "dealers", label: "Dealers", icon: Building2 },
  { id: "dealer-analytics", label: "Dealer Analytics", icon: BarChart2 },
  { id: "salespeople", label: "Salespeople", icon: Users },
  { id: "sales-teams", label: "Sales Teams", icon: Layers },
  { id: "company-performance", label: "Company Performance", icon: BarChart2 },
  { id: "commissions", label: "Commissions", icon: DollarSign },
  { id: "ai-insights", label: "AI Insights", icon: Brain },
  { id: "ai-intelligence", label: "AI Intelligence", icon: Sparkles },
  { id: "pricing", label: "Pricing", icon: Settings },
  { id: "codes", label: "Codes", icon: DollarSign },
  { id: "messaging", label: "Messaging", icon: MessageSquare },
  { id: "rbac", label: "RBAC", icon: Shield },
  { id: "vendor-directory", label: "Vendor Directory", icon: Store },
  { id: "feedback", label: "Feedback", icon: MessageSquare },
  { id: "dealer-feed", label: "Lot Talk", icon: Users },
  { id: "upload-diagnostics", label: "Upload Diagnostics", icon: Bug },
  { id: "api-docs", label: "API Docs", icon: BookOpen },
  { id: "env-health", label: "Env Health", icon: ShieldCheck },
  { id: "telemetry", label: "Telemetry Dashboards", icon: Activity, superAdminOnly: true },
  { id: "database-training", label: "Database Training", icon: Database, superAdminOnly: true },
];

function AdminEnvBadge() {
  const isProd = import.meta.env.PROD;
  const prodUrl = import.meta.env.VITE_PROD_URL as string | undefined;
  const devUrl = import.meta.env.VITE_DEV_URL as string | undefined;
  const label = isProd ? "PROD" : "DEV";
  const counterpartUrl = isProd ? (devUrl || null) : (prodUrl || null);

  const badge = (
    <Badge
      data-testid="badge-admin-env"
      className={`text-xs font-mono px-2 py-0.5 select-none ${
        isProd
          ? "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700"
          : "bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700"
      }`}
      variant="outline"
    >
      {label}
    </Badge>
  );

  if (counterpartUrl) {
    return (
      <a
        href={counterpartUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="link-admin-env-switch"
        title={`Switch to ${isProd ? "DEV" : "PROD"}`}
      >
        {badge}
      </a>
    );
  }

  return badge;
}

// ─── Section fallback ─────────────────────────────────────────────────────────

function SectionFallback() {
  return (
    <div className="space-y-3">
      {Array(3).fill(0).map((_, i) => (
        <Skeleton key={i} className="h-24 w-full" />
      ))}
    </div>
  );
}

// ─── Admin Page Shell ─────────────────────────────────────────────────────────

export default function AdminPage() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const [secret, setSecret] = useState("");
  const [authed, setAuthed] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const section = useMemo<AdminSection>(() => {
    const params = new URLSearchParams(search);
    const s = params.get("section") as AdminSection;
    return NAV_ITEMS.some((n) => n.id === s) ? s : "command-center";
  }, [search]);

  function setSection(s: AdminSection) {
    navigate(`/admin?section=${s}`);
  }
  const [pendingLeaderId, setPendingLeaderId] = useState<string | null>(null);
  const [dealerCohortFilter, setDealerCohortFilter] = useState<string | null>(null);
  const [highlightDealerId, setHighlightDealerId] = useState<string | null>(null);
  const { toast } = useToast();

  const { data: currentUser } = useQuery<{ id: string; role: string; email?: string; isSuperAdmin?: boolean } | null>({
    queryKey: ["/api/auth/me"],
    retry: false,
  });
  useEffect(() => {
    const role = (currentUser as any)?.role;
    if (currentUser && (role === "admin" || role === "owner") && !authed) {
      setAuthed(true);
    }
  }, [currentUser, authed]);

  const { data: users = [], isLoading: usersLoading, refetch: refetchUsers } = useQuery<AdminUser[]>({
    queryKey: ["/api/admin/users", secret],
    queryFn: async () => {
      const res = await adminFetch("/api/admin/users", secret);
      if (!res.ok) throw new Error((await res.json()).message ?? "Unauthorized");
      const data: AdminUser[] = await res.json();
      const subscribers = data.filter((u) => u.role === "subscriber");
      console.log(`[admin/users] fetched total=${data.length} subscribers=${subscribers.length} active=${subscribers.filter((u) => u.subscriptionStatus === "active" || u.subscriptionStatus === "canceling").length}`);
      return data;
    },
    enabled: authed,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const { data: salespeople = [], isLoading: spLoading, refetch: refetchSp } = useQuery<AdminUser[]>({
    queryKey: ["/api/admin/salespeople", secret],
    queryFn: async () => {
      const res = await adminFetch("/api/admin/salespeople", secret);
      if (!res.ok) throw new Error((await res.json()).message ?? "Unauthorized");
      return res.json();
    },
    enabled: authed,
    retry: false,
  });

  const { data: commissions = [], isLoading: commLoading, refetch: refetchComm } = useQuery<CommissionRow[]>({
    queryKey: ["/api/admin/commissions", secret],
    queryFn: async () => {
      const res = await adminFetch("/api/admin/commissions", secret, { method: "POST" });
      if (!res.ok) throw new Error((await res.json()).message ?? "Unauthorized");
      return res.json();
    },
    enabled: authed,
    retry: false,
  });

  const { data: leaderTeamStats = {} } = useQuery<Record<string, { teamMrr: number; teamDealerCount: number; memberCount: number }>>({
    queryKey: ["/api/admin/leader-team-stats", secret],
    queryFn: async () => {
      const res = await adminFetch("/api/admin/leader-team-stats", secret);
      if (!res.ok) return {};
      return res.json();
    },
    enabled: authed,
    retry: false,
  });

  const { data: dealerList = [], isLoading: dealersLoading, refetch: refetchDealers } = useQuery<AdminUser[]>({
    queryKey: ["/api/admin/subscribers", secret],
    queryFn: async () => {
      const res = await adminFetch("/api/admin/subscribers", secret);
      if (!res.ok) throw new Error((await res.json()).message ?? "Unauthorized");
      const data: AdminUser[] = await res.json();
      const serverCount = res.headers.get("X-Subscriber-Count");
      console.log(`[admin/subscribers] endpoint returned ${data.length} subscribers (server reported: ${serverCount})`);
      return data;
    },
    enabled: authed,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  async function handleUnlock(e: React.FormEvent) {
    e.preventDefault();
    setAuthed(true);
    try {
      const res = await adminFetch("/api/admin/users", secret);
      if (!res.ok) {
        setAuthed(false);
        toast({ title: "Access denied", description: "Incorrect admin secret.", variant: "destructive" });
      }
    } catch {
      setAuthed(false);
      toast({ title: "Connection error", variant: "destructive" });
    }
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-2">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <CardTitle>Admin Access</CardTitle>
            <CardDescription>Enter your admin secret to continue (or log in as an admin user)</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUnlock} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="admin-secret">Admin Secret <span className="text-muted-foreground text-xs">(optional if logged in as admin)</span></Label>
                <div className="relative">
                  <Input
                    id="admin-secret"
                    type={showSecret ? "text" : "password"}
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    placeholder="Enter admin secret (or leave blank if admin user)"
                    data-testid="input-admin-secret"
                    autoComplete="off"
                  />
                  <Button type="button" size="icon" variant="ghost"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setShowSecret(!showSecret)}
                    data-testid="button-toggle-secret-visibility">
                    {showSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>
              <Button type="submit" className="w-full" data-testid="button-admin-login">Unlock Admin</Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const dealers = users.filter((u) => u.role === "subscriber");

  const activeSection = NAV_ITEMS.find((n) => n.id === section);

  return (
    <div className="h-screen bg-background flex overflow-hidden">
      {/* Sidebar */}
      <aside className={`${sidebarCollapsed ? "w-16" : "w-56"} shrink-0 border-r bg-card flex flex-col transition-all duration-200 h-screen`}>
        <div className="flex items-center gap-2 px-4 py-4 border-b">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Shield className="w-4 h-4 text-primary" />
          </div>
          {!sidebarCollapsed && <span className="font-semibold text-sm">DealerMind Admin</span>}
        </div>
        <div className="px-2 pt-2">
          <button
            onClick={() => navigate("/")}
            data-testid="button-back-to-app"
            className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm font-medium transition-colors text-muted-foreground hover:bg-muted hover:text-foreground ${sidebarCollapsed ? "justify-center" : ""}`}
            title="Back to App"
          >
            <ChevronLeft className="w-4 h-4 shrink-0" />
            {!sidebarCollapsed && <span className="truncate">Back to App</span>}
          </button>
        </div>
        <nav className="flex-1 py-2 px-2 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.filter((item) => !item.superAdminOnly || currentUser?.isSuperAdmin).map((item) => {
            const Icon = item.icon;
            const active = section === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setSection(item.id)}
                data-testid={`nav-${item.id}`}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm font-medium transition-colors ${
                  active
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
              </button>
            );
          })}
        </nav>
        <div className="p-2 border-t">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            data-testid="button-toggle-sidebar"
          >
            {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 h-full overflow-y-auto">
        <div className="max-w-5xl mx-auto p-6 space-y-6">
          <div className="flex items-center gap-2">
            {activeSection && <activeSection.icon className="w-5 h-5 text-muted-foreground" />}
            <h1 className="text-xl font-bold">{activeSection?.label}</h1>
            <div className="ml-auto">
              <AdminEnvBadge />
            </div>
          </div>

          <Suspense fallback={<SectionFallback />}>
            {section === "command-center" && (
              <CommandCenterSection
                secret={secret}
                dealers={dealers}
                salespeople={salespeople}
                commissions={commissions}
                onNavigate={(s, filter?, dealerId?) => {
                  setSection(s);
                  if (filter !== undefined) setDealerCohortFilter(filter);
                  if (dealerId !== undefined) setHighlightDealerId(dealerId);
                }}
              />
            )}
            {section === "dealers" && (
              <DealersSection
                dealers={dealerList}
                salespeople={salespeople}
                secret={secret}
                isLoading={dealersLoading}
                onRefresh={() => { refetchDealers(); refetchSp(); refetchComm(); }}
                toast={toast}
                cohortFilter={dealerCohortFilter}
                highlightDealerId={highlightDealerId}
                onClearFilter={() => { setDealerCohortFilter(null); setHighlightDealerId(null); }}
              />
            )}
            {section === "salespeople" && (
              <SalespeopleSection salespeople={salespeople} commissions={commissions} dealers={dealerList} secret={secret} isLoading={spLoading}
                onRefresh={() => { refetchSp(); refetchComm(); refetchDealers(); }} toast={toast} leaderTeamStats={leaderTeamStats} />
            )}
            {section === "sales-teams" && (
              <SalesTeamsTab
                secret={secret}
                salespeople={salespeople}
                commissions={commissions}
                dealers={dealerList}
                pendingLeaderId={pendingLeaderId}
                onPendingLeaderHandled={() => setPendingLeaderId(null)}
              />
            )}
            {section === "company-performance" && (
              <CompanyPerformanceTab
                secret={secret}
                onNavigate={setSection}
                isSuperAdmin={currentUser?.isSuperAdmin ?? false}
                onCreateTeamForLeader={(leaderId) => {
                  setPendingLeaderId(leaderId);
                  setSection("sales-teams");
                }}
              />
            )}
            {section === "commissions" && (
              <CommissionsSection commissions={commissions} isLoading={commLoading} secret={secret} onRefresh={refetchComm} />
            )}
            {section === "ai-insights" && (
              <AIInsightsSection secret={secret} onNavigate={setSection} />
            )}
            {section === "pricing" && (
              <PricingTab secret={secret} dealers={dealers} salespeople={salespeople} />
            )}
            {section === "codes" && (
              <CodesSection secret={secret} />
            )}
            {section === "messaging" && (
              <MessagingTab secret={secret} dealers={dealers} />
            )}
            {section === "rbac" && (
              <RbacTab secret={secret} currentUserIsSuperAdmin={currentUser?.isSuperAdmin ?? false} allUsers={users} />
            )}
            {section === "vendor-directory" && (
              <VendorDirectoryTab secret={secret} />
            )}
            {section === "feedback" && (
              <FeedbackTab secret={secret} />
            )}
            {section === "dealer-feed" && (
              <DealerFeedTab />
            )}
            {section === "ai-intelligence" && (
              <AiIntelligencePage />
            )}
            {section === "upload-diagnostics" && (
              <UploadDiagnosticsSection secret={secret} />
            )}
            {section === "dealer-analytics" && (
              <DealerAnalyticsSection secret={secret} />
            )}
            {section === "api-docs" && (
              <ApiDocsSection />
            )}
            {section === "telemetry" && (currentUser?.isSuperAdmin ?? false) && (
              <TelemetrySection />
            )}
            {section === "database-training" && (currentUser?.isSuperAdmin ?? false) && (
              <DatabaseTrainingSection />
            )}
            {section === "env-health" && (
              <EnvHealthSection
                secret={secret}
                isSuperAdmin={currentUser?.isSuperAdmin ?? false}
                currentUser={currentUser ? { id: currentUser.id, email: currentUser.email } : null}
              />
            )}
          </Suspense>
        </div>
      </main>
    </div>
  );
}
